import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useLocation } from 'react-router';
import { type Flow } from 'types/appointments';
import { ConfirmationStep } from '../components/steps/ConfirmationStep';
import { DataStep } from '../components/steps/DataStep';
import { PsychologistStep } from '../components/steps/PsychologistStep';
import { SearchPatientStep } from '../components/steps/SearchPatient';
import { SessionStep } from '../components/steps/SessionStep';
import {
  type AppointmentsReserveFormData,
  AppointmentsReservePayload,
  AppointmentSteps,
  type StepProps,
  type Steps,
} from '../types';

export const INITIAL_STEP_INDEX = 0;

type StepsByFlow = {
  [FLOW_MODES.regular]: typeof STEPS;
  [FLOW_MODES.search_patient]: typeof SEARCH_PATIENT_STEPS;
  [FLOW_MODES.search]: typeof SEARCH_STEPS;
  [FLOW_MODES.edit]: typeof EDIT_STEPS;
};

export type StepDefinition = {
  id: number;
  name: Steps;

  component: React.ComponentType<StepProps>;
};

const STEPS: ReadonlyArray<{ name: Steps; component: React.ComponentType<StepProps> }> = [
  { name: AppointmentSteps.data, component: DataStep },
  { name: AppointmentSteps.session, component: SessionStep },
  { name: AppointmentSteps.confirmation, component: ConfirmationStep },
];

const SEARCH_PATIENT_STEPS: ReadonlyArray<{
  name: Steps;
  component: React.ComponentType<StepProps>;
}> = [
  { name: AppointmentSteps.patient, component: SearchPatientStep },
  { name: AppointmentSteps.data, component: DataStep },
  { name: AppointmentSteps.session, component: SessionStep },
  { name: AppointmentSteps.confirmation, component: ConfirmationStep },
];

const SEARCH_STEPS: ReadonlyArray<{
  name: Steps;
  component: React.ComponentType<StepProps>;
}> = [
  { name: AppointmentSteps.data, component: DataStep },
  { name: AppointmentSteps.psychologist, component: PsychologistStep },
  { name: AppointmentSteps.session, component: SessionStep },
  { name: AppointmentSteps.confirmation, component: ConfirmationStep },
];

const EDIT_STEPS: ReadonlyArray<{
  name: Steps;
  component: React.ComponentType<StepProps>;
}> = [
  { name: AppointmentSteps.session, component: SessionStep },
  { name: AppointmentSteps.confirmation, component: ConfirmationStep },
];

const STEPS_BY_FLOW: StepsByFlow = {
  [FLOW_MODES.regular]: STEPS,
  [FLOW_MODES.search_patient]: SEARCH_PATIENT_STEPS,
  [FLOW_MODES.search]: SEARCH_STEPS,
  [FLOW_MODES.edit]: EDIT_STEPS,
};

interface UseStepNavigationResult {
  steps: StepDefinition[];
  currentStepIndex: number;
  currentStep: StepDefinition;
  handleNext: () => void;
  handleBack: (index?: number) => void;
}

const getInitialStepName = (rawStep: number | Steps | undefined, flow: Flow): Steps => {
  if (typeof rawStep === 'string' && rawStep in AppointmentSteps) {
    return rawStep as Steps;
  }
  return STEPS[Number(rawStep)]?.name ?? STEPS_BY_FLOW[flow as keyof StepsByFlow][0].name;
};

export const useStepNavigation = (): UseStepNavigationResult => {
  const { state } = useLocation();
  const { watch } = useFormContext<AppointmentsReserveFormData>();
  const flow = watch(AppointmentsReservePayload.flow);
  const stepsByFlow = STEPS_BY_FLOW[flow as keyof StepsByFlow];

  const steps = useMemo<StepDefinition[]>(
    () => stepsByFlow.map((step, index) => ({ ...step, id: index + 1 })),
    [stepsByFlow]
  );

  const [currentStepName, setCurrentStepName] = useState<Steps>(() =>
    getInitialStepName(state?.step, flow)
  );

  const [prevFlow, setPrevFlow] = useState<Flow>(flow);

  if (flow !== prevFlow) {
    setPrevFlow(flow);
    if (flow === FLOW_MODES.search || prevFlow === FLOW_MODES.search) {
      setCurrentStepName(stepsByFlow[1].name);
    } else {
      setCurrentStepName(stepsByFlow[0].name);
    }
  }

  const currentStepIndex = useMemo(() => {
    const index = steps.findIndex(step => step.name === currentStepName);
    return index === -1 ? 0 : index;
  }, [steps, currentStepName]);

  const currentStep = steps[currentStepIndex] ?? steps[0];

  const handleNext = (): void => {
    const nextIndex = Math.min(currentStepIndex + 1, steps.length - 1);
    setCurrentStepName(steps[nextIndex].name);
  };

  const handleBack = (index?: number): void => {
    const prevIndex = index ?? Math.max(currentStepIndex - 1, 0);
    setCurrentStepName(steps[prevIndex].name);
  };

  return {
    steps,
    currentStepIndex,
    currentStep,
    handleNext,
    handleBack,
  };
};
